import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Phone, PhoneOff, X, Minus, Delete, Loader2, Mic, MicOff, Settings } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { applyMask } from "./PhoneInput";
import { toast } from "./ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

export default function DialerWidget() {
  const appearance = useAppearance();
  const tc = appearance.colors?.["500"] || "#3B82F6";

  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [callStatus, setCallStatus] = useState("idle"); // idle | setup | dialing | ringing | connected | ended
  const updateCallStatus = useCallback((newStatus) => {
    callStatusRef.current = newStatus;
    setCallStatus(newStatus);
  }, []);
  const [callTimer, setCallTimer] = useState(0);
  const [muted, setMuted] = useState(false);
  const [myExtension, setMyExtension] = useState(null);
  const [gotoConnected, setGotoConnected] = useState(false);
  const [webrtcReady, setWebrtcReady] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [phoneLookup, setPhoneLookup] = useState(null); // {contato_nome, negocio_titulo}
  const lookupTimer = useRef(null);

  const timerRef = useRef(null);
  const pcRef = useRef(null);         // RTCPeerConnection
  const streamRef = useRef(null);     // local MediaStream (microphone)
  const remoteAudioRef = useRef(null); // <audio> element for remote audio
  const currentCallId = useRef("");
  const callStatusRef = useRef("idle");
  const recorderRef = useRef(null);   // MediaRecorder for call recording
  const audioChunksRef = useRef([]);
  const callStartTimeRef = useRef(null);
  const callPhoneRef = useRef("");
  const callNegocioIdRef = useRef("");
  const callLeadIdRef = useRef("");
  const wasAnsweredRef = useRef(false);

  // ── Init: check GoTo connection + WebRTC device status ──
  useEffect(() => {
    axios.get(`${API}/goto/config`).then(r => {
      setGotoConnected(r.data.connected);
      window.__goto_connected = r.data.connected;
      if (r.data.connected) {
        axios.get(`${API}/goto/my-extension`).then(r2 => {
          if (r2.data.found) setMyExtension(r2.data);
        }).catch(() => {});
        axios.get(`${API}/goto/webrtc/status`).then(r3 => {
          setWebrtcReady(r3.data.ready);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  // ── Lookup contact by phone (debounced) ──
  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const digits = number.replace(/\D/g, "");
    if (digits.length < 8) { setPhoneLookup(null); return; }
    lookupTimer.current = setTimeout(() => {
      axios.get(`${API}/contatos/lookup-phone?phone=${digits}`).then(r => {
        setPhoneLookup(r.data.found ? r.data : null);
      }).catch(() => setPhoneLookup(null));
    }, 400);
    return () => { if (lookupTimer.current) clearTimeout(lookupTimer.current); };
  }, [number]);

  const fmtTimer = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const cleanupWebRTC = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    currentCallId.current = "";
  }, []);

  // Start recording — mix local + remote into single track via AudioContext
  const audioCtxRef = useRef(null);
  const startRecording = useCallback((localStream, remoteStream) => {
    try {
      if (!remoteStream || remoteStream.getAudioTracks().length === 0) {
        console.warn("[Dialer] No remote audio tracks for recording");
        return;
      }

      // Use AudioContext to MIX both streams into one mono output
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();

      // Connect remote audio
      const remoteSource = ctx.createMediaStreamSource(remoteStream);
      remoteSource.connect(dest);
      console.log("[Dialer] Recording: remote audio mixed in");

      // Connect local mic
      if (localStream && localStream.getAudioTracks().length > 0) {
        const localSource = ctx.createMediaStreamSource(localStream);
        localSource.connect(dest);
        console.log("[Dialer] Recording: local mic mixed in");
      }

      // Record the mixed output (single track)
      const mixedStream = dest.stream;
      console.log("[Dialer] Mixed stream tracks:", mixedStream.getAudioTracks().length);

      let mimeType = "";
      for (const mt of ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", ""]) {
        if (!mt || MediaRecorder.isTypeSupported(mt)) { mimeType = mt; break; }
      }
      console.log("[Dialer] Recording mimeType:", mimeType || "browser default");

      const recorder = new MediaRecorder(mixedStream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start();
      recorderRef.current = recorder;
      callStartTimeRef.current = Date.now();
      console.log("[Dialer] Recording started (AudioContext mix)!");
    } catch (err) {
      console.error("[Dialer] Recording start error:", err);
    }
  }, []);

  // Stop recording and upload for transcription (only if call was answered)
  const stopAndUploadRecording = useCallback(() => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") {
      console.log("[Dialer] No active recorder to stop");
      return;
    }
    if (!wasAnsweredRef.current) {
      console.log("[Dialer] Call was not answered, discarding recording");
      recorderRef.current.stop();
      recorderRef.current = null;
      audioChunksRef.current = [];
      return;
    }
    console.log("[Dialer] Stopping recorder...");
    recorderRef.current.stop();
    recorderRef.current.onstop = async () => {
      const chunks = audioChunksRef.current;
      console.log(`[Dialer] Recorder stopped. Chunks: ${chunks.length}`);
      if (chunks.length === 0) {
        console.log("[Dialer] No audio chunks, skipping upload");
        return;
      }
      const blob = new Blob(chunks, { type: "audio/webm" });
      const durationSec = callStartTimeRef.current ? Math.round((Date.now() - callStartTimeRef.current) / 1000) : 0;
      console.log(`[Dialer] Audio blob: ${(blob.size / 1024).toFixed(1)}KB, duration: ${durationSec}s`);

      // Filters: must have real conversation
      if (durationSec < 10) {
        console.log("[Dialer] Call too short (<10s), skipping transcription");
        return;
      }
      if (blob.size < 5000) {
        console.log("[Dialer] Audio too small (<5KB), likely silence, skipping");
        return;
      }
      // ~1KB per second minimum = real audio; silence produces much less
      const kbPerSec = (blob.size / 1024) / Math.max(durationSec, 1);
      if (kbPerSec < 0.5) {
        console.log(`[Dialer] Audio density too low (${kbPerSec.toFixed(2)} KB/s), likely no conversation`);
        return;
      }

      console.log("[Dialer] Uploading recording for transcription...");
      const fd = new FormData();
      fd.append("audio", blob, "call_recording.webm");
      fd.append("phone", callPhoneRef.current);
      fd.append("duration", String(durationSec));
      fd.append("negocio_id", callNegocioIdRef.current || phoneLookup?.negocio_id || "");
      fd.append("lead_id", callLeadIdRef.current || "");
      try {
        const res = await axios.post(`${API}/goto/call-recording/process`, fd);
        console.log("[Dialer] Backend response:", res.data);
        if (res.data.ok) {
          toast.success("Ligação transcrita e salva nas anotações!");
        } else if (res.data.reason === "no_conversation") {
          console.log("[Dialer] Whisper found no conversation, skipping");
        } else if (res.data.reason === "audio_too_small") {
          console.log("[Dialer] Audio too small for backend");
        }
      } catch (err) {
        console.error("[Dialer] Upload error:", err.response?.data || err.message);
      }
    };
    recorderRef.current = null;
    audioChunksRef.current = [];
  }, [phoneLookup]);

  // ── Timer for call duration ──
  useEffect(() => {
    if (callStatus === "connected") {
      timerRef.current = setInterval(() => setCallTimer(t => t + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (callStatus === "idle") setCallTimer(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [callStatus]);

  // ── Listen for SSE call events from GoTo webhook ──
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const event = detail.event || "";
      const callId = detail.callId || "";
      if (callId && callId === currentCallId.current) {
        if (event === "ringing") updateCallStatus("ringing");
        else if (event === "connected") updateCallStatus("connected");
        else if (event === "ended") {
          toast.info("Chamada encerrada.");
          updateCallStatus("ended");
          setTimeout(() => { updateCallStatus("idle"); setNumber(""); }, 2000);
          cleanupWebRTC();
        }
      }
    };
    window.addEventListener("pipe4:goto_call_event", handler);
    return () => window.removeEventListener("pipe4:goto_call_event", handler);
  }, [cleanupWebRTC]);

  // ── Listen for external dial requests (from deal/lead modals) ──
  const autoDialRef = useRef(false);
  useEffect(() => {
    const handler = (e) => {
      const phone = e.detail?.phone || "";
      if (phone) {
        setNumber(phone.replace(/\D/g, ""));
        callNegocioIdRef.current = e.detail?.negocio_id || "";
        callLeadIdRef.current = e.detail?.lead_id || "";
        setOpen(true);
        autoDialRef.current = true; // flag to auto-call once open
      }
    };
    window.addEventListener("pipe4:dial", handler);
    return () => window.removeEventListener("pipe4:dial", handler);
  }, []);

  // Auto-dial after number is set from external event
  useEffect(() => {
    if (autoDialRef.current && number && webrtcReady && callStatus === "idle") {
      autoDialRef.current = false;
      handleCall();
    }
  }, [number, webrtcReady, callStatus]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── WebRTC Device Setup ──
  const handleSetup = async () => {
    setSettingUp(true);
    try {
      const res = await axios.post(`${API}/goto/webrtc/setup`);
      if (res.data.ok) {
        setWebrtcReady(true);
        toast.success(`Dispositivo WebRTC configurado! Ramal: ${res.data.extension_number}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro no setup WebRTC";
      toast.error(msg);
      console.error("[Dialer] Setup error:", msg);
    }
    setSettingUp(false);
  };

  // ── Make WebRTC Call ──
  const handleCall = async () => {
    if (!number.trim()) return;
    const digits = number.replace(/\D/g, "");
    callPhoneRef.current = digits;
    wasAnsweredRef.current = false;

    updateCallStatus("setup");

    try {
      // Step 1: Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;

      // Step 2: Create RTCPeerConnection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pcRef.current = pc;

      // Add microphone track to connection
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Detect remote hangup via ICE + connection state
      const handleCallEnded = (reason) => {
        if (callStatusRef.current !== "idle" && callStatusRef.current !== "ended") {
          console.log("[Dialer] Remote hangup/reject detected, status was:", callStatusRef.current, "reason:", reason);
          const msgs = {
            disconnected: "Chamada encerrada pelo outro lado.",
            failed: "Chamada falhou. Verifique a conexão.",
            rejected: "Chamada rejeitada.",
            track_ended: "Chamada encerrada.",
          };
          toast.info(msgs[reason] || "Chamada encerrada.");
          stopAndUploadRecording();
          updateCallStatus("ended");
          setTimeout(() => { updateCallStatus("idle"); setNumber(""); }, 2000);
          cleanupWebRTC();
        }
      };

      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        console.log("[Dialer] ICE state:", state);
        if (state === "failed") handleCallEnded("failed");
        else if (state === "disconnected" || state === "closed") handleCallEnded("disconnected");
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        console.log("[Dialer] Connection state:", state);
        if (state === "connected") {
          updateCallStatus("connected");
          wasAnsweredRef.current = true;
          // Start recording ONLY when call is answered
          const remoteStream = remoteAudioRef.current?.srcObject;
          if (!recorderRef.current && remoteStream) {
            console.log("[Dialer] Call answered — starting recording");
            startRecording(streamRef.current, remoteStream);
          }
        } else if (state === "failed") {
          handleCallEnded("failed");
        } else if (state === "disconnected" || state === "closed") {
          handleCallEnded("disconnected");
        }
      };

      // Monitor remote audio track
      pc.ontrack = (event) => {
        console.log("[Dialer] ontrack fired, streams:", event.streams.length);
        if (remoteAudioRef.current && event.streams[0]) {
          remoteAudioRef.current.srcObject = event.streams[0];
          remoteAudioRef.current.play().catch(() => {});

          const remoteTrack = event.streams[0].getAudioTracks()[0];
          if (remoteTrack) {
            console.log("[Dialer] Remote audio track ready, enabled:", remoteTrack.enabled, "muted:", remoteTrack.muted);
            remoteTrack.onended = () => {
              console.log("[Dialer] Remote audio track ended");
              handleCallEnded("track_ended");
            };
            remoteTrack.onmute = () => {
              console.log("[Dialer] Remote audio track muted");
              setTimeout(() => {
                if (remoteTrack.muted && callStatusRef.current !== "connected") {
                  handleCallEnded("rejected");
                }
              }, 3000);
            };
          }
        }
      };

      // Step 3: Create SDP offer
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering to complete (or timeout after 3s)
      await new Promise((resolve) => {
        if (pc.iceGatheringState === "complete") return resolve();
        const timeout = setTimeout(resolve, 3000);
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === "complete") {
            clearTimeout(timeout);
            resolve();
          }
        };
      });

      const sdp = pc.localDescription?.sdp || offer.sdp;

      updateCallStatus("dialing");

      // Step 4: Send SDP to backend → GoTo API → get SDP answer
      const res = await axios.post(`${API}/goto/webrtc/call`, {
        phone: digits,
        sdp: sdp,
      });

      if (res.data.ok && res.data.sdp) {
        currentCallId.current = res.data.callId || "";

        // Step 5: Set remote SDP answer
        await pc.setRemoteDescription(new RTCSessionDescription({
          type: "answer",
          sdp: res.data.sdp,
        }));

        // Status will be updated via SSE webhook (ringing → connected → ended)
        // But set a fallback
        if (res.data.status === "CONNECTING") {
          updateCallStatus("ringing");
        }
        toast.success("Chamada WebRTC iniciada!");
      } else {
        throw new Error(res.data.error || "GoTo não retornou SDP");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erro ao ligar";
      toast.error(msg);
      console.error("[Dialer] Call error:", msg);
      updateCallStatus("idle");
      cleanupWebRTC();
    }
  };

  const handleHangup = () => {
    stopAndUploadRecording();
    updateCallStatus("ended");
    cleanupWebRTC();
    setTimeout(() => { updateCallStatus("idle"); setNumber(""); }, 2000);
  };

  const toggleMute = () => {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMuted(!track.enabled);
      }
    }
  };

  const pressKey = (key) => setNumber(prev => prev + key);

  if (!gotoConnected) return null;

  const isInCall = callStatus !== "idle" && callStatus !== "ended" && callStatus !== "setup";
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const ddi = appearance.default_ddi || "55";
  const displayNumber = number ? `+${ddi} ${applyMask(number, phoneMask)}` : "";
  const statusLabel = {
    setup: "Preparando...",
    dialing: "Discando...",
    ringing: "Chamando...",
    connected: fmtTimer(callTimer),
    ended: "Encerrada",
  }[callStatus] || "";

  const statusColor = {
    setup: "text-slate-400 animate-pulse",
    dialing: "text-slate-400 animate-pulse",
    ringing: "text-slate-400 animate-pulse",
    connected: "text-slate-500",
    ended: "text-slate-400",
  }[callStatus] || "";

  return createPortal(
    <>
      {/* Hidden audio element for remote call audio */}
      <audio ref={remoteAudioRef} autoPlay playsInline data-testid="dialer-remote-audio" />

      {/* FAB Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-all hover:scale-110 z-[9999]"
          style={{ backgroundColor: tc }}
          data-testid="dialer-fab">
          <Phone className="w-6 h-6" />
        </button>
      )}

      {/* Dialer Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 w-72 bg-white rounded-2xl shadow-2xl z-[9999] overflow-hidden" data-testid="dialer-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: tc }}>
            <div className="flex items-center gap-2 text-white">
              <Phone className="w-4 h-4" />
              <span className="text-sm font-semibold">Telefone</span>
              {webrtcReady && <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded-full">WebRTC</span>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setOpen(false)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/20 text-white/80">
                <Minus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Extension info */}
          {myExtension && (
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
              <span>Ramal {myExtension.extension}</span>
              <span className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${webrtcReady ? "bg-emerald-500" : "bg-amber-400"}`} />
                {myExtension.name}
              </span>
            </div>
          )}

          {/* WebRTC Setup needed */}
          {!webrtcReady && (
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
              <p className="text-xs text-amber-700 mb-2">
                Configure o dispositivo WebRTC para ligar pelo navegador.
              </p>
              <button
                onClick={handleSetup}
                disabled={settingUp}
                className="w-full h-8 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 transition-all hover:brightness-110 disabled:opacity-50"
                style={{ backgroundColor: tc }}
                data-testid="dialer-setup-btn">
                {settingUp ? <><Loader2 className="w-3 h-3 animate-spin" />Configurando...</> : <><Settings className="w-3 h-3" />Configurar WebRTC</>}
              </button>
            </div>
          )}

          {/* Number display — formatted with tenant phone mask */}
          <div className="px-4 pt-4 pb-2 h-16 flex items-center">
            {callStatus === "idle" ? (
              <div className="flex items-center w-full">
                <div className="flex-1 min-w-0 text-center">
                  <input
                    value={number ? applyMask(number, phoneMask) : ""}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, "");
                      setNumber(raw);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" && number.trim() && webrtcReady) {
                        e.preventDefault();
                        handleCall();
                      }
                    }}
                    placeholder="Digite o número"
                    className="w-full text-center text-lg font-mono font-semibold text-slate-800 bg-transparent outline-none tracking-wide"
                    data-testid="dialer-number-input"
                  />
                </div>
                {number && (
                  <button onClick={() => setNumber(prev => prev.slice(0, -1))} className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
                    <Delete className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="w-full text-center">
                <p className="text-lg font-mono font-semibold text-slate-800 tracking-wide truncate">
                  {applyMask(number, phoneMask) || number}
                </p>
              </div>
            )}
          </div>

          {/* Contact lookup result */}
          {phoneLookup && (
            <div className="px-4 pb-1 text-center">
              <p className="text-xs font-semibold text-slate-700 truncate">{phoneLookup.contato_nome}</p>
              {phoneLookup.negocio_titulo && (
                <p className="text-[10px] text-slate-400 truncate">{phoneLookup.negocio_titulo}</p>
              )}
            </div>
          )}

          {/* Status */}
          <div className="text-center h-6 flex items-center justify-center">
            {callStatus !== "idle" && (
              <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
            )}
          </div>

          {/* Keypad — fixed height container */}
          <div className="h-[200px] flex flex-col justify-center">
            {callStatus === "idle" ? (
              <div className="px-6">
                {KEYS.map((row, i) => (
                  <div key={i} className="flex justify-center gap-3 mb-2">
                    {row.map(key => (
                      <button key={key} onClick={() => pressKey(key)}
                        className="w-14 h-11 rounded-xl text-lg font-semibold text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                        data-testid={`dialer-key-${key}`}>
                        {key}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center gap-4">
                <button onClick={toggleMute}
                  className={`rounded-full flex items-center justify-center transition-colors ${muted ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  style={{ width: "5rem", height: "5rem" }}
                  data-testid="dialer-mute-btn">
                  {muted ? <MicOff className="w-7 h-7" /> : <Mic className="w-7 h-7" />}
                </button>
              </div>
            )}
          </div>

          {/* Call / Hangup button */}
          <div className="px-6 pb-5 pt-1">
            {callStatus === "idle" || callStatus === "ended" ? (
              <button onClick={handleCall} disabled={!number.trim() || !webrtcReady || callStatus === "ended"}
                className="w-full h-11 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: "#10b981" }}
                data-testid="dialer-call-btn">
                <Phone className="w-4 h-4" />Ligar
              </button>
            ) : callStatus === "setup" ? (
              <button disabled className="w-full h-11 rounded-xl bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 opacity-70">
                <Loader2 className="w-4 h-4 animate-spin" />Preparando microfone...
              </button>
            ) : (
              <button onClick={handleHangup}
                className="w-full h-11 rounded-xl bg-rose-500 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:bg-rose-600"
                data-testid="dialer-hangup-btn">
                <PhoneOff className="w-4 h-4" />Desligar
              </button>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
